import { useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useNavigate } from "react-router";

import {
	IChangePasswordRequest,
	IResetPasswordRequest,
	IRestorePasswordRequest,
	IUserSignInRequest,
	authenticationAPI,
} from "@api/authentication";
import { processRequest } from "@api/config";

import { useChatStore } from "@storage/global/useChatStore";
import { useIntroLogoStore } from "@storage/global/useIntroLogoStore";
import { useMessengerStore } from "@storage/global/useMessengerStore";
import { useWebSocketStore } from "@storage/global/useWebSocketStore";
import { TUserAuthData } from "@storage/session/types";
import { appTokenStorage, isLoggingOutStorage, userAuthDataStorage } from "@storage/sessionStorage";

import { sdkMessenger } from "@services/messenger-sdk";
import { closeWebSocket } from "@services/webSocket";

import { API_PREFIXES } from "@configs/api";
import { ROUTES } from "@configs/routes";

export const useSignOutMutation = () => {
	const { resetState: resetMessengerState } = useMessengerStore();
	const { resetState: resetWebSocketState } = useWebSocketStore();
	const { resetState: resetChatState } = useChatStore();
	const messengerSdk = sdkMessenger();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () =>
			processRequest({
				prefix: API_PREFIXES.USER_SIGN_OUT,
				request: (token) => authenticationAPI.userSignOutAsync({ token }),
			}),
		onError: (error) => {
			console.error("Error during sign out:", error);
		},
		onMutate: () => {
			isLoggingOutStorage.setItem("true");
		},
		onSuccess: async () => {
			navigate(ROUTES.LOGIN, { replace: true });

			messengerSdk.authManager.logoutAsync();
			sessionStorage.clear();

			closeWebSocket(true);

			resetMessengerState();
			resetWebSocketState();
			resetChatState();

			await queryClient.cancelQueries();
			queryClient.clear();
		},
	});
};

export const useSignInMutation = () => {
	const [isLoading, setIsLoading] = useState(false);
	const navigate = useNavigate();
	const { show: showIntroLogo } = useIntroLogoStore();

	const mutation = useMutation({
		mutationFn: async ({ username, password }: IUserSignInRequest) =>
			processRequest({
				prefix: API_PREFIXES.USER_SIGN_IN,
				request: () => authenticationAPI.userSignInAsync({ username, password }),
			}),
		onError: () => {
			setIsLoading(false);
		},
		onMutate: () => {
			setIsLoading(true);
		},
		onSuccess: (data) => {
			appTokenStorage.setItem(data.userSessionToken);
			userAuthDataStorage.setItemAsJson<TUserAuthData>({
				app_mute: false,
				audio_chat_muted: false,
				audio_chat_volume: 1,
				bss_username: data.username,
				chatClientId: data.chatClientId,
				roomChatId: data.roomChatId,
				isIndividual: data.isIndividual,
				userId: data.userId,
				email: data.email,
				isShowIntroLogo: false,
			});

			showIntroLogo();

			const checkRedirectUrl = Cookies.get("redirect_url");
			if (checkRedirectUrl) {
				navigate(checkRedirectUrl);
				Cookies.remove("redirect_url");
			} else {
				navigate(ROUTES.HOME);
			}
		},
	});

	return { signInMutate: mutation.mutate, isLoading };
};

export const useChangePasswordMutation = () =>
	useMutation({
		mutationFn: async ({ oldPassword, newPassword, newPasswordRepeat }: IChangePasswordRequest) =>
			processRequest({
				prefix: API_PREFIXES.CHANGE_PASSWORD,
				request: (token) =>
					authenticationAPI.changePasswordAsync({ token, oldPassword, newPassword, newPasswordRepeat }),
			}),
	});

export const useRestorePasswordMutation = () =>
	useMutation({
		mutationFn: async ({ email, username, captchaResponse }: IRestorePasswordRequest) =>
			processRequest({
				prefix: API_PREFIXES.RESTORE_PASSWORD,
				request: () => authenticationAPI.restorePasswordAsync({ email, username, captchaResponse }),
			}),
	});

export const useResetPasswordMutation = () =>
	useMutation({
		mutationFn: async ({ restoreId, email, username, password, captchaResponse }: IResetPasswordRequest) =>
			processRequest({
				prefix: API_PREFIXES.RESET_PASSWORD,
				request: () => authenticationAPI.resetPasswordAsync({ restoreId, email, username, password, captchaResponse }),
			}),
	});
